import React from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { useCart } from "../context/CartContext";
import { formatAUD } from "../data/pricing";
import { formatAUDexact } from "../data/signsPricing";
import { Link } from "react-router-dom";
import { Trash2, ArrowRight, ShoppingBag, Truck } from "lucide-react";

export default function Cart() {
  const { items, removeItem, totals } = useCart();
  return (
    <div className="App">
      <Header />
      <section className="max-w-7xl mx-auto px-5 lg:px-8 pt-12 pb-16">
        <div className="eyebrow">Your cart</div>
        <h1 className="font-display text-4xl sm:text-5xl font-black mt-2">Review your order</h1>

        {items.length === 0 ? (
          <div className="sf-card p-14 mt-8 text-center" data-testid="cart-empty">
            <ShoppingBag size={32} className="mx-auto text-slate-400" />
            <div className="mt-4 font-display text-2xl font-extrabold">Your cart is empty</div>
            <p className="text-slate-500 mt-2">Pick a design or create a custom sticker to get started.</p>
            <div className="mt-6 flex gap-3 justify-center">
              <Link to="/stickers" className="btn-primary">Shop stickers</Link>
              <Link to="/signs" className="btn-outline">Shop signs</Link>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mt-8">
            <div className="lg:col-span-8 space-y-4">
              {items.map((i) => (
                <div key={i.cart_id} className="sf-card p-5 flex gap-4" data-testid={`cart-item-${i.cart_id}`}>
                  <div className="w-24 h-24 rounded-xl overflow-hidden bg-[#F3EFEA] flex-shrink-0 flex items-center justify-center">
                    {i.design_preview ? <img src={i.design_preview} alt="" className="w-full h-full object-cover"/> :
                      ["corflute", "foamboard", "pvc", "pullup", "coaster", "flag", "aframe", "mediawall"].includes(i.product_type) ?
                      <div className="w-full h-full flex items-center justify-center flex-col p-2 text-center bg-[#003DA5] text-white">
                        <div className="font-display font-black text-xs tracking-tight uppercase leading-tight">{{ corflute: "Corflute", foamboard: "Foamboard", pvc: "PVC", pullup: "Pull-Up", coaster: "Coaster", flag: "Flag", aframe: "A-Frame", mediawall: "Media Wall" }[i.product_type]}</div>
                        <div className="font-mono-brand text-[10px] mt-0.5">{i.product_type === "pullup" ? "850×2000" : i.size}</div>
                      </div> :
                      <div className="w-full h-full flex items-center justify-center font-mono-brand text-xs text-slate-500 p-2 text-center">CUSTOM<br/>UPLOAD</div>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="eyebrow">{labelForType(i.product_type)}{i.needs_quote && <span className="ml-2 text-[#003DA5]">· Custom quote</span>}</div>
                    <div className="font-display font-extrabold text-lg leading-tight truncate">{i.product_name}</div>
                    <div className="text-sm text-slate-600 mt-1">
                      {i.shape ? `${i.shape} · ` : ""}{i.size} · {i.quantity.toLocaleString()} {unitWordForType(i.shape === "Sticker Sheet" ? "sheet" : i.product_type, i.quantity)}
                      {i.print_sides && <> · {i.print_sides === "double" ? "Double-sided" : "Single-sided"}</>}
                      {i.product_type === "corflute" && i.eyelets > 0 && <> · {i.eyelets} eyelets/board</>}
                    </div>
                    {i.kinds > 1 && <div className="text-xs text-slate-600 mt-1" data-testid={`cart-kinds-${i.cart_id}`}>{i.qty_per_design.toLocaleString()} per design × {i.kinds} designs</div>}
                    {i.artwork_filename && <div className="text-xs text-slate-500 mt-1 truncate">Artwork: {i.artwork_filename}</div>}
                  </div>
                  <div className="text-right flex flex-col items-end justify-between">
                    <div className="font-display font-black text-xl price-tick">
                      {i.needs_quote ? <span className="text-[#003DA5] text-sm font-bold tracking-wide uppercase">Quote</span> : formatAUDexact(i.line_total)}
                    </div>
                    <button className="btn-ghost text-slate-500 hover:!text-[#E04826]" onClick={() => removeItem(i.cart_id)} data-testid={`cart-remove-${i.cart_id}`}>
                      <Trash2 size={16} /> Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <aside className="lg:col-span-4">
              <div className="sf-card p-6 sticky top-20">
                <div className="eyebrow">Order summary</div>
                <div className="mt-4 space-y-2 text-sm">
                  <Row label="Subtotal" value={formatAUDexact(totals.subtotal)} testid="cart-subtotal"/>
                  <Row label="GST (10%)" value={formatAUDexact(totals.gst)} testid="cart-gst"/>
                  <Row
                    label="Delivery"
                    value="Choose at checkout"
                    testid="cart-shipping"
                  />
                </div>
                <div className="mt-3 p-3 rounded-xl bg-[#F3EFEA] text-xs text-slate-600 font-semibold flex items-start gap-2" data-testid="cart-delivery-note">
                  <Truck size={14} className="mt-0.5 flex-shrink-0" />
                  <span>Choose delivery or pickup at checkout. Delivery costs are confirmed after you order, based on weight and destination.</span>
                </div>
                <div className="h-px bg-[#E2DDD5] my-4"/>
                <div className="flex justify-between items-baseline">
                  <div className="font-display font-bold">Total</div>
                  <div className="font-display font-black text-2xl price-tick" data-testid="cart-total">{formatAUDexact(totals.total)}</div>
                </div>
                <Link to="/checkout" className="btn-primary w-full mt-5" data-testid="cart-checkout-btn">
                  Checkout <ArrowRight size={16}/>
                </Link>
              </div>
            </aside>
          </div>
        )}
      </section>
      <Footer />
    </div>
  );
}

function Row({ label, value, testid, highlight }) {
  return (
    <div className="flex justify-between py-1" data-testid={testid}>
      <span className="text-slate-500">{label}</span>
      <span className={`font-semibold ${highlight ? "text-[#10B981]" : ""}`}>{value}</span>
    </div>
  );
}

function labelForType(t) {
  if (t === "corflute") return "Corflute sign";
  if (t === "foamboard") return "Foamboard";
  if (t === "pvc") return "3mm PVC sign";
  if (t === "pullup") return "Pull-up banner";
  if (t === "coaster") return "Printed coasters";
  if (t === "flag") return "Feather flag";
  if (t === "aframe") return "Aluminium A-Frame";
  if (t === "mediawall") return "Media wall";
  if (t === "custom") return "Custom sticker";
  return "Ready-made";
}
function unitWordForType(t, qty) {
  const one = qty === 1;
  if (t === "corflute" || t === "foamboard" || t === "pvc") return one ? "board" : "boards";
  if (t === "pullup") return one ? "banner" : "banners";
  if (t === "coaster") return one ? "coaster" : "coasters";
  if (t === "sheet") return one ? "sheet" : "sheets";
  if (t === "flag") return one ? "flag" : "flags";
  if (t === "aframe") return one ? "A-Frame" : "A-Frames";
  if (t === "mediawall") return one ? "media wall" : "media walls";
  return one ? "sticker" : "stickers";
}
